<script lang="ts">
  import type { CloudImage } from "$lib/def/seo/cloud-image";
  import type { Insert } from "$lib/def/seo/insert";
  import type { ServiceArea } from "$lib/def/seo/service-area";
  import type { Setting } from "$lib/def/seo/setting";
  import type { Page } from "@sveltejs/kit";
  
	import { Collection } from "$lib/def/seo/_general";
	import { createTextEditorAction } from "$lib/serv/admin/edit";
	import { editable$, editing$, flattenCollection, get, getSEOPageExtras } from "$lib/serv/seo";
	import { addDoc } from "$lib/serv/vendor-integration/firebase";
	import { combineLatest, map, Observable, of, startWith, Subject, switchMap } from "rxjs";

	import Editor from "$lib/ui/cms/Editor.svelte";
	import Button from "$lib/ui/form/Button.svelte";

	export let seoExtras: { id?:string, slug?:string, line?: string, paragraph?: string, image?: CloudImage, slugSuggestion?: string } | undefined = undefined;
	
	/**
	 * Needed for inline editing of the additional SEO content
	 */
	export let page: Page | undefined = undefined;

	type SEOData = [boolean, Insert[]?, Setting<any>[]?, ServiceArea?];
	type Extras = ReturnType<typeof getSEOPageExtras> & {editing:boolean};

	const subjRefreshDocId = new Subject<{id:string,slug:string}>();

	const data$ = editing$.pipe(
		switchMap(editing => editing ? combineLatest([
			of(true),
			get<Insert>(Collection.Inserts).then(list => flattenCollection(list ?? [])),
			get<Setting>(Collection.Settings).then(list => flattenCollection(list ?? [])),
			get<ServiceArea>(Collection.Areas).then(list => flattenCollection(list ?? [])).then(list => list?.find(area => {
				const [a,b] = page?.params.area.split('/') ?? ['', ''];
				if (b) return area.slug === b && area.childOf === a;
				return area.slug === a;
			}))
		]) as Observable<SEOData> : of([false]) as Observable<SEOData>),
	);

	const edit$:Observable<Extras> = combineLatest([
		data$,
		subjRefreshDocId.pipe(startWith({id:'',slug:''})),
	]).pipe(
		map(([[editing, inserts, settings, entity], {id,slug}]) => {
			if (!editing) return { editing, ...seoExtras } as Extras;
			let extras: ReturnType<typeof getSEOPageExtras> | undefined;
			if (page?.route) extras = getSEOPageExtras(page?.route, page?.url.pathname, inserts, settings, entity)
			if (extras) return { editing, ...extras, id: extras.id ?? id, slug: extras.slug ?? slug };
			return { editing, ...seoExtras } as Extras;
		}),
		startWith({editing:false} as Extras),
	)

	// let docId = '';
	let addedSlug = '';
	let adding = false;
	const add = async (slug: string) => {
		adding = true;
		const id = await addDoc(Collection.Inserts, { slug }) ?? '';
		subjRefreshDocId.next({id,slug});
		addedSlug = slug;
		adding = false;
	}
</script>

{#if $editable$}
	<Editor />
{/if}

{#if seoExtras}
	{@const { editing, line, paragraph, slug, id, slugSuggestion } = $edit$}
	{#key id}<!-- needed to recreate `editText` -->
		{#if id || editing}
			{@const editText = id ? createTextEditorAction(Collection.Inserts, id) : ()=>{}}
			<div class="body-l book">
				{#if line || editing}
					<h4 use:editText={['title']} class="headline-m">{line}</h4>
				{/if}
				{#if paragraph || editing}
					<p use:editText={['subtitle']} class="body-l book">{paragraph}</p>
				{/if}
			</div>
			{#if editing}
				{#if slug}
					<em class="label-xs book">
						Insert slug: <a href={`/cms/inserts?tab=2&slug=${encodeURIComponent(slug ?? '')}`}>{slug ?? '[not set]'}</a>
					</em>
				{:else if slugSuggestion}
					<em class="label-xs book">
						Insert not yet created. Suggested slug: <a href={`/cms/inserts?tab=2&slug=${encodeURIComponent(slugSuggestion)}`}>{slugSuggestion}</a>
					</em>
					<div>
						<Button onClick={() => add(slugSuggestion)} class="button primary small" loading={adding} disabled={adding}>Create insert using this slug</Button>
					</div>
				{/if}
			{/if}
		{/if}
	{/key}
{/if}
